import { assign, includes, isEqual } from 'lodash';

import * as actions from '@common-modules/mediaDetails/actions';
import {
  BoundingPoly,
  BoundingPolyRect,
} from '@common-modules/mediaDetails/models';
import { WorkerState, namespace } from '../../models';
import { defaultState } from '../../store';
import { addGapToAutoDetectedSeries } from './gaps';
import { selectDetectionClusterGroupsSubset } from '../../selectors/clusters';
import {
  boundingPolyFromPolyCenter,
  polyCenterFromBoundingPoly,
  scaleBoundingPolyRect,
  frameAlignStartTimeMs,
  frameAlignStopTimeMs,
} from '@utils';
import {
  DETECTION_TYPES,
  DEFAULT_GLOBAL_SETTINGS,
  convertPolyToScalable,
  GROUPED_POLY_TYPE,
} from '@helpers/constants';
import { getBoundingPolySize } from '@worker/helpers/BoundingPoly';
import { CaseReducer, createReducer, Draft, original } from '@reduxjs/toolkit';
import { produce, produceWithPatches } from 'immer';
import { GroupedBoundingPoly } from '@common/web-worker/timeline-collection';

type Re<P = unknown> = CaseReducer<WorkerState, { payload: P; type: string }>;

function markOverlayAsDeleted(state: Draft<WorkerState>, id: string): void {
  const obj = state.objectMap[id];

  if (obj) {
    obj.object.isDeleted = true;
  }
}

export const onMarkOverlayAsDeleted: Re<{
  readonly boundingBox: GroupedBoundingPoly;
}> = (state, { payload }) => {
  const boundingBox = payload.boundingBox;

  const [nextState, patches, inversePatches] = produceWithPatches(
    state,
    (draft) => {
      if (includes(DETECTION_TYPES, boundingBox.type)) {
        const boundingPoly = draft.objectMap[boundingBox.id];
        if (!boundingPoly) {
          return;
        }

        // markOverlayAsDeleted(draft, boundingBox.id); // OLD delete method
        deleteDetectionSpan(
          draft,
          boundingBox,
          boundingPoly.startTimeMs,
          boundingPoly.stopTimeMs
        );
      }
    }
  );

  return produce(nextState, (draft) => {
    draft.history.past.push({ patches, inversePatches });
    draft.history.future = [];
  });
};

export const onMarkOverlayInFrameAsDeleted: Re<{
  readonly boundingBox: GroupedBoundingPoly;
  readonly timeMs: number;
}> = (state, { payload }) => {
  const boundingBox = payload.boundingBox;

  const [nextState, patches, inversePatches] = produceWithPatches(
    state,
    (draft) => {
      deleteDetectionSpan(
        draft,
        boundingBox,
        payload.timeMs,
        payload.timeMs + 1
      );
    }
  );

  return produce(nextState, (draft) => {
    draft.history.past.push({ patches, inversePatches });
    draft.history.future = [];
  });
};

function deleteDetectionSpan(
  draft: Draft<WorkerState>,
  boundingBox: {
    type: GROUPED_POLY_TYPE;
    id: string;
    subsegmentId: string;
  },
  startTimeMs: number,
  stopTimeMs: number
): void {
  const { subsegmentId } = boundingBox;

  const fps = draft.tdo?.details?.veritoneFile?.videoFrameRate || 30;

  const frameStartTimeMs = Math.ceil(frameAlignStartTimeMs(startTimeMs, fps));
  const frameStopTimeMs = Math.floor(frameAlignStopTimeMs(stopTimeMs, fps));

  const nextFrameStartTimeMs = Math.ceil(
    frameAlignStartTimeMs(frameStopTimeMs + 1, fps)
  );
  const prevFrameStopTimeMs = Math.floor(
    frameAlignStopTimeMs(frameStartTimeMs - 1, fps)
  );

  const currSubsegment = draft.subsegmentMap[subsegmentId];
  if (!currSubsegment) {
    return;
  }
  const groupId = currSubsegment.groupId;

  const group = draft.groupMap[groupId];
  if (!group) {
    return;
  }
  let subsegmentIndex = group.subsegmentIds.indexOf(subsegmentId);
  if (subsegmentIndex === -1) {
    return;
  }

  const boundingPoly = draft.objectMap[boundingBox.id];
  if (!boundingPoly) {
    return;
  }

  const currStartTimeMs = Math.ceil(
    frameAlignStartTimeMs(boundingPoly.startTimeMs, fps)
  );
  const currStopTimeMs = Math.floor(
    frameAlignStartTimeMs(boundingPoly.stopTimeMs, fps)
  );

  // build up left and right end objects
  const leftSubsegmentObjectIds: string[] = [];
  const rightSubsegmentObjectIds: string[] = [];

  for (const objectId of currSubsegment.objectIds || []) {
    const subsegmentObject = draft.objectMap[objectId];
    if (subsegmentObject) {
      if (subsegmentObject.stopTimeMs <= currStartTimeMs) {
        leftSubsegmentObjectIds.push(objectId);
      }
      if (subsegmentObject.startTimeMs >= currStopTimeMs) {
        rightSubsegmentObjectIds.push(objectId);
      }
    }
  }

  // split off new left-end object from current object if necessary
  if (currStartTimeMs < frameStartTimeMs) {
    const leftId = `${currStartTimeMs}-${prevFrameStopTimeMs}-${groupId}`;
    const leftBoundingPoly: BoundingPoly = {
      ...boundingPoly,
      id: leftId,
      startTimeMs: currStartTimeMs,
      stopTimeMs: prevFrameStopTimeMs,
      object: {
        ...boundingPoly.object,
        id: leftId,
      },
    };
    draft.objectMap[leftId] = leftBoundingPoly; // add new object
    leftSubsegmentObjectIds.unshift(leftId);
  }

  // create new left subsegment
  if (leftSubsegmentObjectIds.length > 0) {
    const leftSubsegmentId = `${currSubsegment.startTimeMs}-${prevFrameStopTimeMs}-${groupId}`;
    draft.subsegmentMap[leftSubsegmentId] = {
      id: leftSubsegmentId,
      objectIds: leftSubsegmentObjectIds,
      startTimeMs: currSubsegment.startTimeMs,
      stopTimeMs: prevFrameStopTimeMs,
      groupId,
    };
    group.subsegmentIds.splice(subsegmentIndex, 0, leftSubsegmentId);
    subsegmentIndex = subsegmentIndex + 1;
    draft.selectedPolyGroups[leftSubsegmentId] = true; // set as selected

    // update subsegmentId for left subsegment objects
    for (const objectId of leftSubsegmentObjectIds || []) {
      if (draft.objectMap[objectId]) {
        draft.objectMap[objectId].subsegmentId = leftSubsegmentId;
      }
    }
  }

  // split off new right-end object from current object if necessary
  if (currStopTimeMs > frameStopTimeMs) {
    const rightObjectId = `${nextFrameStartTimeMs}-${currStopTimeMs}-${groupId}`;
    const rightBoundingPoly: BoundingPoly = {
      ...boundingPoly,
      id: rightObjectId,
      startTimeMs: nextFrameStartTimeMs,
      stopTimeMs: currStopTimeMs,
      object: {
        ...boundingPoly.object,
        id: rightObjectId,
      },
    };

    draft.objectMap[rightObjectId] = rightBoundingPoly; // add new object
    rightSubsegmentObjectIds.unshift(rightObjectId);
  }

  // mark original object as deleted - so does not re-interpolate
  const newCenterBoundingPoly = {
    ...boundingPoly,
    id: `${frameStartTimeMs}-${frameStopTimeMs}-${groupId}`,
    startTimeMs: frameStartTimeMs,
    stopTimeMs: frameStopTimeMs,
    object: {
      ...boundingPoly.object,
      isDeleted: true,
    },
  };

  // delete original subsegment / object
  delete draft.objectMap[boundingPoly.id];
  delete draft.subsegmentMap[subsegmentId]; // remove from subsegment map
  delete draft.selectedPolyGroups[subsegmentId];
  group.subsegmentIds.splice(subsegmentIndex, 1);

  // create new center subsegment
  draft.objectMap[newCenterBoundingPoly.id] = newCenterBoundingPoly;
  const centerSubsegmentId = `${frameStartTimeMs}-${frameStopTimeMs}-${groupId}`;
  draft.subsegmentMap[centerSubsegmentId] = {
    id: centerSubsegmentId,
    objectIds: [newCenterBoundingPoly.id],
    startTimeMs: frameStartTimeMs,
    stopTimeMs: frameStopTimeMs,
    groupId,
  };
  group.subsegmentIds.splice(subsegmentIndex, 0, centerSubsegmentId); // add to group

  if (rightSubsegmentObjectIds.length > 0) {
    // create new right subsegment
    const rightSubsegmentId = `${nextFrameStartTimeMs}-${currSubsegment.stopTimeMs}-${groupId}`;
    draft.subsegmentMap[rightSubsegmentId] = {
      id: rightSubsegmentId,
      objectIds: rightSubsegmentObjectIds,
      startTimeMs: nextFrameStartTimeMs,
      stopTimeMs: currSubsegment.stopTimeMs,
      groupId,
    };
    group.subsegmentIds.splice(subsegmentIndex + 1, 0, rightSubsegmentId); // add to group
    draft.selectedPolyGroups[rightSubsegmentId] = true; // set as selected

    // update subsegmentId for right subsegment objects
    for (const objectId of rightSubsegmentObjectIds || []) {
      if (draft.objectMap[objectId]) {
        draft.objectMap[objectId].subsegmentId = rightSubsegmentId;
      }
    }
  }

  // check if entire group has been deleted
  const isDeleted = group.subsegmentIds
    .map((subsegmentId) => {
      const subsegmentIsDeleted = draft.subsegmentMap[subsegmentId]?.objectIds
        .map((objectId) => draft.objectMap[objectId]?.object.isDeleted)
        .every((v) => v === true);
      return subsegmentIsDeleted;
    })
    .every((v) => v === true);

  if (isDeleted) {
    deleteDetectionGroupHelper(draft, draft, groupId);
  }
}

// Update changes into groupMap when delete overlay subsegment
export const onDeleteOverlaySegment: Re<actions.DeleteSegmentPayload> = (
  state,
  action
) => {
  const { timeMs, segmentId, groupId, type } = action.payload;

  if (!includes(DETECTION_TYPES, type)) {
    return;
  }

  if (!groupId) {
    return;
  }

  const [nextState, patches, inversePatches] = produceWithPatches(
    state,
    (draft) => {
      const clusterGroup = selectDetectionClusterGroupsSubset([groupId])({
        [namespace]: draft,
      })[groupId];

      if (!clusterGroup) {
        return;
      }

      // if only one segment in group then delete entire group
      if (clusterGroup.segments.length === 1) {
        deleteDetectionGroupHelper(draft, state, groupId);
        return;
      }

      // find segment
      let segment;
      if (segmentId) {
        // faster to search by segmentId when available
        segment = clusterGroup.segments.find(
          (segment) => segment.id === segmentId
        );
      } else {
        segment = clusterGroup.segments.find(
          (segment) =>
            segment.startTimeMs <= timeMs && segment.stopTimeMs > timeMs
        );
      }

      if (!segment) {
        return;
      }

      const headSubsegmentObjectIds = segment.objectIds;

      const gapIds = headSubsegmentObjectIds.filter(
        (headSubsegmentId) => !draft.objectMap[headSubsegmentId]
      );

      for (const gapId of gapIds) {
        addGapToAutoDetectedSeries(draft, gapId, groupId);
      }

      // remove from is selected
      for (const trueSubsegmentId of segment.subsegmentIds) {
        delete draft.selectedPolyGroups[trueSubsegmentId];
      }

      for (const hgoId of headSubsegmentObjectIds) {
        markOverlayAsDeleted(draft, hgoId);
      }
    }
  );

  return produce(nextState, (draft) => {
    draft.history.past.push({ patches, inversePatches });
    draft.history.future = [];
  });
};

// Update changes into groupMap when delete overlay group
export const onDeleteOverlayGroup: Re<{
  readonly boundingBox: GroupedBoundingPoly;
}> = (state, { payload }) => {
  if (!includes(DETECTION_TYPES, payload.boundingBox.type)) {
    return;
  }
  const { groupId } = payload.boundingBox;
  if (!groupId) {
    return;
  }

  const [nextState, patches, inversePatches] = produceWithPatches(
    state,
    (draft) => {
      deleteDetectionGroupHelper(draft, state, groupId);
    }
  );

  return produce(nextState, (draft) => {
    draft.history.past.push({ patches, inversePatches });
    draft.history.future = [];
  });
};

export const removeDetectionGroupFromClusterMapItem = (
  draft: Draft<WorkerState>,
  clusterId: string | undefined,
  groupId: string
) => {
  // remove groupId from the clusterMap[boundingPolysGroup.clusterId].detectionGroupIds
  if (clusterId) {
    const clusterMapItem = draft.clusterMap[clusterId];
    if (clusterMapItem) {
      const index = clusterMapItem.detectionGroupIds.indexOf(groupId);
      if (index > -1) {
        clusterMapItem.detectionGroupIds.splice(index, 1);
      }

      // if cluster has no remaining assigned groups it can be removed
      if (
        clusterMapItem.detectionGroupIds.length +
          clusterMapItem.udrGroupIds.length ===
        0
      ) {
        delete draft.clusterMap[clusterId];
      }
    }
  }
};

export const deleteDetectionGroupHelper = (
  draft: Draft<WorkerState>,
  state: Draft<WorkerState>,
  groupId: string
) => {
  const group = state.groupMap[groupId];
  if (!group) {
    return;
  }
  delete draft.selectedPolyGroups[groupId]; // this isn't really necessary since we in theory should no longer be saving groupIds

  group.subsegmentIds.forEach((subsegmentId: string) => {
    state.subsegmentMap[subsegmentId]?.objectIds?.forEach((objectId) => {
      const obj = draft.objectMap[objectId];
      if (obj) {
        obj.object.isDeleted = true;
      }
    });
    delete draft.selectedPolyGroups[subsegmentId]; // remove all group subsegments
  });

  // remove gaps
  for (const gapId of Object.keys(state.detectionGaps[groupId] || {})) {
    delete draft.selectedPolyGroups[gapId];
  }

  // remove groupId from the clusterMap[boundingPolysGroup.clusterId].detectionGroupIds
  const clusterId = group.clusterId;
  removeDetectionGroupFromClusterMapItem(draft, clusterId, groupId);
};

// Update changes objectMap directly instead of traverse through clusterList
const onChangeOverlay: Re<{
  id: string;
  groupId: string;
  boundingPoly: BoundingPolyRect;
  type: GROUPED_POLY_TYPE;
}> = (state, { payload }) => {
  const [newState, patches, inversePatches] = produceWithPatches(
    state,
    (draft) => {
      const bp = draft.objectMap[payload.id];

      if (!includes(DETECTION_TYPES, payload.type) || !bp) {
        return;
      }

      let boundingPoly = payload.boundingPoly;

      // for types that get scaled math is more complicated
      if (includes(DETECTION_TYPES, payload.type)) {
        // rather than just update boundingPoly, recalculate the poly center and unscaled size
        // this will lead to better behavior around borders and scaling

        // load previous boundingPoly and polyCenter
        const prevBoundingPoly =
          original(state)?.objectMap[payload.id]?.object.boundingPoly;
        if (!prevBoundingPoly) {
          return;
        }
        const prevPolyCenter = polyCenterFromBoundingPoly(prevBoundingPoly);

        // now get scaled versions - this was what was previously on screen
        const type = convertPolyToScalable(payload.type);
        const prevBoundingPolyScaled = scaleBoundingPolyRect(
          prevBoundingPoly,
          type
            ? (original(state)?.tdo?.details?.settings?.objectTypeEffects[type]
                .scaling ??
                DEFAULT_GLOBAL_SETTINGS.objectTypeEffects[type].scaling)
            : 0
        );

        if (isEqual(prevBoundingPolyScaled, boundingPoly)) {
          return;
        }

        const prevPolyCenterScaled = polyCenterFromBoundingPoly(
          prevBoundingPolyScaled
        );

        // calc new scaled poly center
        const newPolyCenterScaled = polyCenterFromBoundingPoly(boundingPoly);

        // update polyCenter using the shift to the center, and relative change in shape
        const polyCenter = {
          x:
            prevPolyCenter.x + (newPolyCenterScaled.x - prevPolyCenterScaled.x),
          y:
            prevPolyCenter.y + (newPolyCenterScaled.y - prevPolyCenterScaled.y),
          width:
            prevPolyCenter.width *
            (newPolyCenterScaled.width / prevPolyCenterScaled.width),
          height:
            prevPolyCenter.height *
            (newPolyCenterScaled.height / prevPolyCenterScaled.height),
        };

        boundingPoly = boundingPolyFromPolyCenter(polyCenter);
      }

      bp.object.boundingPoly = boundingPoly;
      bp.version = (bp.version ?? 0) + 1;
    }
  );

  return produce(newState, (draft) => {
    draft.history.past.push({ patches, inversePatches });
    draft.history.future = [];
  });
};

function promoteGapsToSeriesItems(
  state: Draft<WorkerState>,
  gapIds: string[],
  groupId: string
): void {
  for (const gapId of gapIds) {
    addGapToAutoDetectedSeries(state, gapId, groupId);
  }
}

export const onResizeOverlaySegment: Re<{
  readonly boundingBox: GroupedBoundingPoly;
}> = (state, { payload }) => {
  const origState = original<Readonly<WorkerState>>(state);
  if (!origState || !includes(DETECTION_TYPES, payload.boundingBox.type)) {
    return;
  }

  const [nextState, patches, inversePatches] = produceWithPatches(
    state,
    (draft) => {
      const { startTimeMs, id, boundingPoly, groupId } = payload.boundingBox;

      const clusterGroup = selectDetectionClusterGroupsSubset([groupId])({
        [namespace]: state,
      })[groupId];

      if (!clusterGroup) {
        return;
      }

      // find segment
      const segment = clusterGroup.segments.find(
        (segment) =>
          segment.startTimeMs <= startTimeMs &&
          segment.stopTimeMs >= startTimeMs
      );

      if (!segment) {
        return;
      }

      const headSubsegmentObjectIds = segment.objectIds;

      const gapIds = headSubsegmentObjectIds.filter(
        (headSubsegmentId) => !origState.objectMap[headSubsegmentId]
      );

      promoteGapsToSeriesItems(draft, gapIds, groupId);

      const type =
        payload.boundingBox.type === 'licensePlate'
          ? 'plate'
          : payload.boundingBox.type;

      const scaleFactor =
        type !== undefined &&
        type !== 'udr' &&
        type !== 'poim' &&
        type !== 'text_overlay'
          ? 100 /
            (100 +
              (draft.tdo?.details?.settings?.objectTypeEffects?.[type]
                .scaling ??
                DEFAULT_GLOBAL_SETTINGS.objectTypeEffects[type].scaling))
          : 1;

      // get newSize - undo the scaling
      const newSize = {
        width: (boundingPoly[1].x - boundingPoly[0].x) * scaleFactor,
        height: (boundingPoly[2].y - boundingPoly[1].y) * scaleFactor,
      };

      // this uses the unscaled size from when video was loaded - or last subsegment resize
      const objectMapItem = draft.objectMap[id];
      if (!objectMapItem) {
        return;
      }
      const originalSize = objectMapItem.size;
      // let objectMap = newState.objectMap;
      const objectMap = draft.objectMap;

      const horizontalScale =
        ((newSize.width - originalSize.width) / originalSize.width) * 100;
      const verticalScale =
        ((newSize.height - originalSize.height) / originalSize.height) * 100;

      const scaler = resizeHeadSubsegment(horizontalScale, verticalScale);

      for (const headSubsegmentId of headSubsegmentObjectIds) {
        const item = objectMap[headSubsegmentId];
        if (!item) {
          continue;
        }

        if (headSubsegmentId === id) {
          item.size = newSize;
        } else {
          const modifiedItem = scaler(item);
          const size = getBoundingPolySize(modifiedItem.object.boundingPoly);
          item.object = modifiedItem.object;
          item.size = size;
          item.version = (item.version ?? 0) + 1;
        }
      }
    }
  );

  return produce(nextState, (draft) => {
    draft.history.past.push({ patches, inversePatches });
    draft.history.future = [];
  });
};

const resizeHeadSubsegment =
  (horizontalScale: number, verticalScale: number) =>
  (initItem: BoundingPoly) => {
    let item = initItem;
    const { boundingPoly } = item.object;

    const polyCenter = polyCenterFromBoundingPoly(boundingPoly);

    // resize
    const newPolyCenter = {
      x: polyCenter.x,
      y: polyCenter.y,
      width: polyCenter.width * (1 + horizontalScale / 100),
      height: polyCenter.height * (1 + verticalScale / 100),
    };

    const newBoundingPoly = boundingPolyFromPolyCenter(newPolyCenter);

    item = assign({}, item, {
      object: assign({}, item.object, {
        boundingPoly: newBoundingPoly,
      }),
    });

    return item;
  };

export default createReducer(defaultState, (builder) => {
  builder
    .addCase(actions.DELETE_DETECTION_OVERLAY, onMarkOverlayAsDeleted)
    .addCase(actions.DELETE_DETECTION_IN_FRAME, onMarkOverlayInFrameAsDeleted)
    .addCase(actions.DELETE_DETECTION_SEGMENT, onDeleteOverlaySegment)
    .addCase(actions.DELETE_DETECTION_GROUP, onDeleteOverlayGroup)
    .addCase(actions.CHANGE_OVERLAY, onChangeOverlay)
    .addCase(actions.RESIZE_OVERLAY_SEGMENT, onResizeOverlaySegment);
});
